import { graphqlRequest } from "@/graphql/client";
import { IS_DEV } from "@/utils/env";
import { DEFAULT_PROCESS_TIMEOUT_MS } from "./processConstants";
import {
  INVOKE_NOT_ACCEPTED,
  MISSING_CORRELATION_ID,
  SUBSCRIPTION_REPORTED_ERROR,
  UNEXPECTED_TERMINAL_EVENT,
} from "./subscriptionUserMessages";

const PROCESS_TRACKING_TAG = "[Process Tracking]";

/**
 * @param {string} processTag
 * @returns {string}
 */
const toFlowTag = (processTag) => (typeof processTag === "string" && processTag.trim().length > 0 ? processTag : "UNKNOWN");

/**
 * @param {string} message
 * @param {Object} [payload]
 * @returns {void}
 */
const logProcessTracking = (message, payload = undefined) => {
  if (!IS_DEV) return;
  if (payload === undefined) {
    console.log(message);
    return;
  }
  console.log(message, payload);
};



/**
 * @param {unknown} value
 * @returns {boolean}
 */
export function isSuccessStatusCode(value) {
  return value === 200 || value === "200";
}

/**
 * @param {string|Object|null|undefined} body
 * @returns {Object|Array<unknown>|null}
 * @throws {Error} When `body` is a string that is not valid JSON
 */
export function parseGraphqlBody(body) {
  if (body == null) return null;
  if (typeof body === "string") {
    try {
      return JSON.parse(body);
    } catch {
      throw new Error("Invalid JSON in GraphQL subscription body");
    }
  }
  if (typeof body === "object") {
    return body;
  }
  return null;
}

/**
 * @param {Object} options
 * @param {Object} options.subscriptionQuery
 * @param {string} options.correlationId
 * @param {number} [options.timeoutMs]
 * @param {(payload: Object) => void} options.onEvent
 * @param {(error: unknown) => void} options.onError
 * @param {string} [options.authMode]
 * @param {string} [options.timeoutErrorMessage]
 * @param {string} [options.processTag]
 * @returns {{ teardown: () => void }}
 */
export function createAppSyncSubscription({
  subscriptionQuery,
  correlationId,
  timeoutMs = DEFAULT_PROCESS_TIMEOUT_MS,
  onEvent,
  onError,
  authMode = "AMAZON_COGNITO_USER_POOLS",
  timeoutErrorMessage = "Subscription timeout - processing took too long",
  processTag = "UNKNOWN",
}) {
  const flowTag = toFlowTag(processTag);
  let subscription = null;
  let timeout = null;

  const teardown = () => {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
    if (subscription) {
      subscription.unsubscribe();
      subscription = null;
    }
    logProcessTracking(`${PROCESS_TRACKING_TAG} [${flowTag}] Subscription teardown`, { correlationId });
  };

  logProcessTracking(`${PROCESS_TRACKING_TAG} [${flowTag}] Subscription setup`, {
    correlationId,
    timeoutMs,
  });
  timeout = setTimeout(() => {
    teardown();
    logProcessTracking(`${PROCESS_TRACKING_TAG} [${flowTag}] Subscription timeout`, {
      correlationId,
      timeoutErrorMessage,
    });
    onError(new Error(timeoutErrorMessage));
  }, timeoutMs);

  try {
    subscription = graphqlRequest({
      query: subscriptionQuery,
      variables: { id: correlationId },
      authMode,
    }).subscribe({
      next: (response) => {
        try {
          const container = response?.data ?? {};
          const eventKey = Object.keys(container)[0];
          const payload = eventKey ? container[eventKey] : null;
          if (!payload || payload.id !== correlationId) return;
          logProcessTracking(`${PROCESS_TRACKING_TAG} [${flowTag}] Subscription event received`, {
            correlationId,
            status: payload?.status ?? null,
            statusCode: payload?.statusCode ?? null,
          });
          onEvent(payload);
        } catch (error) {
          teardown();
          logProcessTracking(`${PROCESS_TRACKING_TAG} [${flowTag}] Subscription next-handler error`, {
            correlationId,
            error: error?.message ?? "Unknown subscription event error",
          });
          onError(error);
        }
      },
      error: (error) => {
        teardown();
        logProcessTracking(`${PROCESS_TRACKING_TAG} [${flowTag}] Subscription transport error`, {
          correlationId,
          error: error?.message ?? "Unknown subscription transport error",
        });
        onError(error);
      },
    });
  } catch (error) {
    teardown();
    logProcessTracking(`${PROCESS_TRACKING_TAG} [${flowTag}] Subscription setup failure`, {
      correlationId,
      error: error?.message ?? "Unknown subscription setup error",
    });
    onError(error);
  }

  return { teardown };
}

/**
 * @param {Object} options
 * @param {Object} options.invokeQuery
 * @param {Object} options.invokeVariables
 * @param {string} options.invokeResultPath
 * @param {Object} options.subscriptionQuery
 * @param {(payload: Object) => boolean} [options.isProcessingEvent]
 * @param {(payload: Object) => Object|Array<unknown>|null} [options.mapDonePayload]
 * @param {number} [options.timeoutMs]
 * @param {string} [options.authMode]
 * @param {string} [options.processTag]
 * @returns {Promise<{correlationId: string, payload: Object|Array<unknown>|null}>}
 */
export async function invokeAndSubscribe({
  invokeQuery,
  invokeVariables,
  invokeResultPath,
  subscriptionQuery,
  isProcessingEvent = (payload) => isSuccessStatusCode(payload?.statusCode) && payload?.status === "processing",
  mapDonePayload = (payload) => parseGraphqlBody(payload?.body),
  timeoutMs = DEFAULT_PROCESS_TIMEOUT_MS,
  authMode = "AMAZON_COGNITO_USER_POOLS",
  processTag = "UNKNOWN",
}) {
  const flowTag = toFlowTag(processTag);
  logProcessTracking(`${PROCESS_TRACKING_TAG} [${flowTag}] Invoking process API`);
  const response = await graphqlRequest({
    query: invokeQuery,
    variables: invokeVariables,
    authMode,
  });

  const invokeResult = invokeResultPath
    .split(".")
    .reduce((acc, key) => (acc && typeof acc === "object" ? acc[key] : undefined), response);

  if (!isSuccessStatusCode(invokeResult?.statusCode)) {
    logProcessTracking(`${PROCESS_TRACKING_TAG} [${flowTag}] Invocation failed`, {
      statusCode: invokeResult?.statusCode ?? "unknown",
    });
    throw new Error(INVOKE_NOT_ACCEPTED);
  }

  const correlationId = invokeResult?.id;
  if (!correlationId) {
    logProcessTracking(`${PROCESS_TRACKING_TAG} [${flowTag}] Invocation missing correlation id`, {
      statusCode: invokeResult?.statusCode ?? null,
    });
    throw new Error(MISSING_CORRELATION_ID);
  }
  logProcessTracking(`${PROCESS_TRACKING_TAG} [${flowTag}] Invocation accepted`, { correlationId });

  return new Promise((resolve, reject) => {
    let settled = false;
    let teardown = () => {};
    const settleReject = (error) => {
      if (settled) return;
      settled = true;
      teardown();
      logProcessTracking(`${PROCESS_TRACKING_TAG} [${flowTag}] Flow rejected`, {
        correlationId,
        error: error?.message ?? "Unknown flow error",
      });
      reject(error);
    };
    const settleResolve = (value) => {
      if (settled) return;
      settled = true;
      teardown();
      logProcessTracking(`${PROCESS_TRACKING_TAG} [${flowTag}] Flow resolved`, { correlationId });
      resolve(value);
    };
    const setup = createAppSyncSubscription({
      subscriptionQuery,
      correlationId,
      timeoutMs,
      authMode,
      processTag: flowTag,
      onEvent: (payload) => {
        if (settled) return;
        if (isProcessingEvent(payload)) return;
        if (payload?.status === "error") {
          logProcessTracking(`${PROCESS_TRACKING_TAG} [${flowTag}] Subscription reported error status`, {
            correlationId,
            statusCode: payload?.statusCode ?? null,
            subscriptionStatus: payload?.status ?? null,
          });
          settleReject(new Error(SUBSCRIPTION_REPORTED_ERROR));
          return;
        }
        if (isSuccessStatusCode(payload?.statusCode) && payload?.status === "done") {
          try {
            const mapped = mapDonePayload(payload);
            settleResolve({ correlationId, payload: mapped });
          } catch (error) {
            settleReject(error);
          }
          return;
        }
        logProcessTracking(`${PROCESS_TRACKING_TAG} [${flowTag}] Unexpected terminal subscription event`, {
          correlationId,
          statusCode: payload?.statusCode ?? null,
          subscriptionStatus: payload?.status ?? null,
        });
        settleReject(new Error(UNEXPECTED_TERMINAL_EVENT));
      },
      onError: settleReject,
    });
    teardown = setup.teardown;
  });
}
